/**
 * Bundle Builder — assets/bundle-builder.js
 *
 * Custom element for sneaker + apparel bundle selection.
 * Supports 2 or 4 product slots (gender toggle, optional product-type toggle).
 * Progressive disclosure: collapsible cards with merged combo selector.
 * Pricing uses the base product's actual price — no discount arithmetic.
 *
 * Cart integration: theme.routes.cartAdd + ajaxProduct:added event
 * Line item properties: _bundleProduct (timestamp), _bundleTitle
 */

if (!customElements.get('bundle-builder')) {
  class BundleBuilder extends HTMLElement {
    constructor() {
      super();

      this.bundleTitle = this.dataset.bundleTitle || 'Bundle';

      // Parse product JSON blobs
      this.baseProduct = this._json('[data-base-product-json]');
      this.products = {
        '1a': this._json('[data-product-1a-json]'),
        '1b': this._json('[data-product-1b-json]'),
        '2a': this._json('[data-product-2a-json]'),
        '2b': this._json('[data-product-2b-json]'),
      };
      this.labels = this._json('[data-bundle-labels]') || {};

      // Does this bundle have a second product-type dimension (4 separate products)?
      this.hasProductType = !!(this.products['2a'] || this.products['2b']);

      // Variant-level type filtering (e.g. Hoodie/Crew as variant option within one product)
      this.hasTypeFilter = false;
      this.typeValues = []; // e.g. ['Hoodie', 'Crew']

      // State
      this.activeGender = this.dataset.defaultGender || 'a'; // 'a' or 'b'
      this.activeType = '1'; // '1' or '2' (product switch OR type filter)
      this.activeTypeFilterValue = null; // actual variant option value when filtering
      this.isMixMode = false;

      // Sneaker
      this.sneakerColour = null;
      this.sneakerSize = null;
      this.sneakerVariantId = null;

      // Apparel
      this.apparelColour = null;
      this.apparelSize = null;
      this.apparelVariantId = null;
    }

    connectedCallback() {
      this._cacheDom();
      this._setupCards();
      this._bind();
      this._initState();
    }

    // ── JSON helper ────────────────────────────────────────────────

    _json(sel) {
      const el = this.querySelector(sel);
      if (!el) return null;
      try { return JSON.parse(el.textContent); }
      catch { return null; }
    }

    // ── DOM cache ──────────────────────────────────────────────────

    _cacheDom() {
      this.sneakerColoursEl = this.querySelector('[data-sneaker-colours]');
      this.sneakerSizesEl = this.querySelector('[data-sneaker-sizes]');
      this.sneakerColourLabel = this.querySelector('[data-sneaker-colour-label]');

      this.matchedState = this.querySelector('[data-matched-state]');
      this.mixState = this.querySelector('[data-mix-state]');
      this.apparelThumb = this.querySelector('[data-apparel-thumb]');
      this.apparelName = this.querySelector('[data-apparel-name]');
      this.apparelMeta = this.querySelector('[data-apparel-meta]');
      this.previewSneaker = this.querySelector('[data-preview-sneaker]');
      this.previewApparel = this.querySelector('[data-preview-apparel]');
      this.mixSwatches = this.querySelector('[data-mix-swatches]');
      this.mixColourValue = this.querySelector('[data-mix-colour-value]');
      this.mismatchNote = this.querySelector('[data-mismatch-note]');
      this.apparelSizesEl = this.querySelector('[data-apparel-sizes]');
      this.backLink = this.querySelector('[data-close-mix]');

      this.summaryTotal = this.querySelector('[data-summary-total]');
      this.summaryIncluded = this.querySelector('[data-summary-included]');

      this.atcBtn = this.querySelector('[data-bundle-atc]');
      this.atcText = this.querySelector('[data-bundle-atc-text]');
      this.sneakerInput = this.querySelector('[data-sneaker-variant-id]');
      this.apparelInput = this.querySelector('[data-apparel-variant-id]');
    }

    // ── Card setup ─────────────────────────────────────────────────

    _setupCards() {
      this.card1 = this.querySelector('#bb-card-1');
      this.card2 = this.querySelector('#bb-card-2');
    }

    // ── Events ─────────────────────────────────────────────────────

    _bind() {
      // Mix mode
      const openMix = this.querySelector('[data-open-mix]');
      if (openMix) openMix.addEventListener('click', () => this._enterMix());
      if (this.backLink) this.backLink.addEventListener('click', () => this._exitMix());

      // Delegated clicks for cards, combos, and dynamic swatches/sizes
      this.addEventListener('click', e => {
        // Card header toggle
        const cardToggle = e.target.closest('[data-card-toggle]');
        if (cardToggle && !e.target.closest('[data-card-edit]')) {
          const n = parseInt(cardToggle.dataset.cardToggle);
          const card = this.querySelector('#bb-card-' + n);
          if (card && card.classList.contains('is-complete')) {
            card.classList.toggle('is-collapsed');
          }
          return;
        }

        // Card edit button
        const cardEdit = e.target.closest('[data-card-edit]');
        if (cardEdit) {
          e.stopPropagation();
          this._editCard(parseInt(cardEdit.dataset.cardEdit));
          return;
        }

        // Combo type+gender selector
        const combo = e.target.closest('[data-combo]');
        if (combo) {
          const c = combo.dataset.combo;
          this._setCombo(c.charAt(0), c.charAt(1));
          return;
        }

        const sc = e.target.closest('[data-sneaker-colour-value]');
        if (sc) { this._pickSneakerColour(sc.dataset.sneakerColourValue); return; }

        const ss = e.target.closest('[data-sneaker-size-value]');
        if (ss && !ss.classList.contains('is-sold-out')) { this._pickSneakerSize(ss.dataset.sneakerSizeValue); return; }

        const ac = e.target.closest('[data-apparel-colour-value]');
        if (ac) { this._pickApparelColour(ac.dataset.apparelColourValue); return; }

        const as = e.target.closest('[data-apparel-size-value]');
        if (as && !as.classList.contains('is-sold-out')) { this._pickApparelSize(as.dataset.apparelSizeValue); return; }
      });

      // Add to cart
      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', e => this._addToCart(e));
      }
    }

    // ── Init ───────────────────────────────────────────────────────

    _initState() {
      if (!this.baseProduct) return;

      const bp = this.baseProduct;
      const cIdx = bp.colour_option_index;
      const sIdx = bp.size_option_index;

      const sel = bp.variants.find(v => v.id === bp.selected_variant_id)
        || bp.variants.find(v => v.available)
        || bp.variants[0];

      if (sel) {
        this.sneakerColour = cIdx >= 0 ? sel.options[cIdx] : null;
        this.sneakerSize = sel.options[sIdx];
        this.sneakerVariantId = sel.id;
      }

      // Detect variant-level type option (e.g. Hoodie/Crew) when no 4-product split
      if (!this.hasProductType) {
        const product = this._activeProduct();
        if (product) {
          this._detectTypeFilter(product);
        }
      }

      // Keep original type toggle hidden when combo selector exists
      const typeToggle = this.querySelector('[data-type-toggle]');
      if (typeToggle) {
        const hasCombo = !!this.querySelector('.bb-combo-types');
        typeToggle.style.display = (this.hasTypeFilter && !hasCombo) ? '' : 'none';
      }

      // Upgrade combo selector to 4-button if type filter detected
      this._buildComboSelector();

      this._syncApparelToSneaker();
      this._render();
    }

    _detectTypeFilter(product) {
      const tIdx = this._typeIdx(product);
      if (tIdx < 0) {
        this.hasTypeFilter = false;
        return;
      }
      this.hasTypeFilter = true;
      const opt = product.options_with_values[tIdx];
      // Map toggle labels to option values via normalised matching
      const label1 = this._n(this.labels.type1Label || '');
      const label2 = this._n(this.labels.type2Label || '');
      if (label1 && label2) {
        const v1 = opt.values.find(v => this._n(v) === label1) || opt.values[0];
        const v2 = opt.values.find(v => this._n(v) === label2) || opt.values[1];
        this.typeValues = [v1, v2];
      } else {
        this.typeValues = [opt.values[0], opt.values[1] || opt.values[0]];
      }
      this.activeTypeFilterValue = this.typeValues[parseInt(this.activeType) - 1] || this.typeValues[0];
    }

    // ── Combo selector ─────────────────────────────────────────────

    _buildComboSelector() {
      const container = this.querySelector('[data-combo-container]');
      if (!container || !this.hasTypeFilter || this.typeValues.length < 2) return;

      // Get gender labels from labels JSON or fallback to toggle button text
      const labelA = this.labels.genderALabel
        || (this.querySelector('[data-gender="a"]')?.textContent.trim())
        || 'A';
      const labelB = this.labels.genderBLabel
        || (this.querySelector('[data-gender="b"]')?.textContent.trim())
        || 'B';

      const type1 = this.typeValues[0];
      const type2 = this.typeValues[1];

      // Update label
      const label = this.querySelector('[data-combo-label]');
      if (label) label.textContent = 'Style & Fit';

      // Build 4 buttons
      const defaultCombo = this.activeType + this.activeGender;
      container.innerHTML = [
        { combo: '1b', label: labelB + ' ' + type1 },
        { combo: '1a', label: labelA + ' ' + type1 },
        { combo: '2b', label: labelB + ' ' + type2 },
        { combo: '2a', label: labelA + ' ' + type2 },
      ].map(function(btn) {
        return '<button type="button" class="bb-combo-type' +
          (btn.combo === defaultCombo ? ' is-active' : '') +
          '" data-combo="' + btn.combo + '">' + btn.label + '</button>';
      }).join('');
    }

    _setCombo(type, gender) {
      this.activeType = type;
      this.activeGender = gender;

      // Sync original toggle button states (hidden but used by internal logic)
      this.querySelectorAll('[data-product-type]').forEach(btn => {
        const active = btn.dataset.productType === type;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
      this.querySelectorAll('[data-gender]').forEach(btn => {
        const active = btn.dataset.gender === gender;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });

      // Update combo button active states
      this.querySelectorAll('[data-combo]').forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.combo === type + gender);
      });

      // Update type filter if applicable
      if (this.hasTypeFilter) {
        this.activeTypeFilterValue = this.typeValues[parseInt(type) - 1] || this.typeValues[0];
      }

      this._onProductSwitch();
    }

    // ── Card collapse/expand ───────────────────────────────────────

    _collapseCard(n) {
      const card = this.querySelector('#bb-card-' + n);
      if (card) {
        card.classList.add('is-complete', 'is-collapsed');
      }
    }

    _expandCard(n) {
      const card = this.querySelector('#bb-card-' + n);
      if (card) {
        card.classList.remove('is-collapsed', 'is-complete');
      }
    }

    _editCard(n) {
      const card = this.querySelector('#bb-card-' + n);
      if (card) {
        card.classList.remove('is-complete', 'is-collapsed');
      }
      if (n === 1) {
        // Also collapse card 2 while re-editing sneaker
        const card2 = this.querySelector('#bb-card-2');
        if (card2) card2.classList.add('is-collapsed');
      }
    }

    _updateCardSummaries() {
      // Card 1 summary
      const summary1 = this.querySelector('[data-card-summary="1"]');
      if (summary1 && this.sneakerSize) {
        const colourPart = this.sneakerColour ? this.sneakerColour + ' — ' : '';
        summary1.textContent = colourPart + 'Size ' + this.sneakerSize;
      }

      // Card 2 summary
      const summary2 = this.querySelector('[data-card-summary="2"]');
      if (summary2 && this.apparelSize) {
        const product = this._activeProduct();
        if (product) {
          let typeLabel = '';
          if (this.hasProductType) {
            typeLabel = this.activeType === '1'
              ? (this.labels.type1Label || 'Option A')
              : (this.labels.type2Label || 'Option B');
          } else if (this.hasTypeFilter) {
            typeLabel = this.activeTypeFilterValue || '';
          }
          const genderA = this.querySelector('[data-gender="a"]');
          const genderB = this.querySelector('[data-gender="b"]');
          let genderLabel = '';
          if (this.activeGender === 'a' && genderA) genderLabel = genderA.textContent.trim();
          if (this.activeGender === 'b' && genderB) genderLabel = genderB.textContent.trim();

          const parts = [genderLabel, typeLabel].filter(Boolean);
          summary2.textContent = parts.join(' ') + ' — ' + this.apparelSize;
        }
      }

      // Sync combo selector active states
      this.querySelectorAll('[data-combo]').forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.combo === this.activeType + this.activeGender);
      });
    }

    // ── Helpers ─────────────────────────────────────────────────────

    _n(s) { return (s || '').toLowerCase().replace(/[\s\-_]/g, ''); }

    _titleCase(s) {
      if (!s) return '';
      return s.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }

    _money(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }

    _activeProduct() {
      // In type filter mode, type toggle filters variants within the same product (always slot 1)
      const typeKey = this.hasTypeFilter ? '1' : this.activeType;
      const key = typeKey + this.activeGender;
      return this.products[key] || this.products['1a'] || this.products['1b'];
    }

    _colourIdx(product) {
      if (!product?.options_with_values) return 0;
      const i = product.options_with_values.findIndex(o => {
        const n = o.name.toLowerCase();
        return n.includes('colour') || n.includes('color');
      });
      return i >= 0 ? i : 0;
    }

    _sizeIdx(product) {
      if (!product?.options_with_values) return 1;
      const i = product.options_with_values.findIndex(o => o.name.toLowerCase().includes('size'));
      return i >= 0 ? i : 1;
    }

    _typeIdx(product) {
      if (!product?.options_with_values) return -1;
      return product.options_with_values.findIndex(o => {
        const n = o.name.toLowerCase();
        return !n.includes('colour') && !n.includes('color') && !n.includes('size');
      });
    }

    _matchColour(sneakerColour, product) {
      if (!product) return null;
      const cIdx = this._colourIdx(product);
      const opt = product.options_with_values[cIdx];
      if (!opt) return null;
      const norm = this._n(sneakerColour);
      const typeVariants = this._variantsForType(product);
      // Only match if the colour exists in the current type-filtered variants
      const colourExists = typeVariants.some(v => this._n(v.options[cIdx]) === norm);
      return colourExists ? opt.values.find(v => this._n(v) === norm) : null;
    }

    _firstAvailableColour(product) {
      if (!product) return null;
      const cIdx = this._colourIdx(product);
      const typeVariants = this._variantsForType(product);
      // Get unique colours from type-filtered variants
      const seen = new Set();
      for (const v of typeVariants) {
        const colour = v.options[cIdx];
        if (!seen.has(this._n(colour)) && v.available) return colour;
        seen.add(this._n(colour));
      }
      // Fallback to first colour in filtered set
      return typeVariants.length ? typeVariants[0].options[cIdx] : null;
    }

    _findVariant(product, colour, size) {
      if (!product) return null;
      const cIdx = this._colourIdx(product);
      const sIdx = this._sizeIdx(product);
      const variants = this._variantsForType(product);
      return variants.find(v =>
        this._n(v.options[cIdx]) === this._n(colour) &&
        this._n(v.options[sIdx]) === this._n(size)
      ) || null;
    }

    _variantsForColour(product, colour) {
      if (!product) return [];
      const cIdx = this._colourIdx(product);
      let variants = product.variants.filter(v => this._n(v.options[cIdx]) === this._n(colour));
      if (this.hasTypeFilter && this.activeTypeFilterValue) {
        const tIdx = this._typeIdx(product);
        if (tIdx >= 0) {
          variants = variants.filter(v => this._n(v.options[tIdx]) === this._n(this.activeTypeFilterValue));
        }
      }
      return variants;
    }

    _variantsForType(product) {
      if (!product) return [];
      if (!this.hasTypeFilter || !this.activeTypeFilterValue) return product.variants;
      const tIdx = this._typeIdx(product);
      if (tIdx < 0) return product.variants;
      return product.variants.filter(v => this._n(v.options[tIdx]) === this._n(this.activeTypeFilterValue));
    }

    // ── Sync apparel colour to sneaker ─────────────────────────────

    _syncApparelToSneaker() {
      const product = this._activeProduct();
      if (!product) return;

      if (!this.isMixMode) {
        // If sneaker has no colour option, go straight to first available apparel colour
        if (!this.sneakerColour) {
          this.apparelColour = this._firstAvailableColour(product);
        } else {
          const match = this._matchColour(this.sneakerColour, product);
          if (match) {
            this.apparelColour = match;
          } else {
            this.isMixMode = true;
            this.apparelColour = this._firstAvailableColour(product);
          }
        }
      }
      this._autoApparelSize();
    }

    _autoApparelSize() {
      const product = this._activeProduct();
      if (!product) return;
      const sIdx = this._sizeIdx(product);
      const variants = this._variantsForColour(product, this.apparelColour);

      // Keep current size if still available
      if (this.apparelSize) {
        const current = variants.find(v => this._n(v.options[sIdx]) === this._n(this.apparelSize) && v.available);
        if (current) { this.apparelVariantId = current.id; return; }
      }

      const first = variants.find(v => v.available);
      if (first) {
        this.apparelSize = first.options[sIdx];
        this.apparelVariantId = first.id;
      } else if (variants.length) {
        this.apparelSize = variants[0].options[sIdx];
        this.apparelVariantId = variants[0].id;
      }
    }

    // ── Sneaker actions ────────────────────────────────────────────

    _pickSneakerColour(colour) {
      this.sneakerColour = colour;
      this._autoSneakerSize();
      if (!this.isMixMode) this._syncApparelToSneaker();
      this._render();
    }

    _pickSneakerSize(size) {
      this.sneakerSize = size;
      const bp = this.baseProduct;
      const cIdx = bp.colour_option_index;
      const sIdx = bp.size_option_index;
      const v = cIdx >= 0
        ? bp.variants.find(v =>
            this._n(v.options[cIdx]) === this._n(this.sneakerColour) &&
            this._n(v.options[sIdx]) === this._n(size))
        : bp.variants.find(v => this._n(v.options[sIdx]) === this._n(size));
      if (v) this.sneakerVariantId = v.id;
      this._render();

      // Auto-collapse card 1 and open card 2
      this._collapseCard(1);
      const card2 = this.querySelector('#bb-card-2');
      if (card2) card2.classList.remove('is-collapsed');
    }

    _autoSneakerSize() {
      const bp = this.baseProduct;
      if (!bp) return;
      const cIdx = bp.colour_option_index;
      const sIdx = bp.size_option_index;
      const variants = cIdx >= 0
        ? bp.variants.filter(v => this._n(v.options[cIdx]) === this._n(this.sneakerColour))
        : bp.variants;

      if (this.sneakerSize) {
        const current = variants.find(v => this._n(v.options[sIdx]) === this._n(this.sneakerSize) && v.available);
        if (current) { this.sneakerVariantId = current.id; return; }
      }

      const first = variants.find(v => v.available);
      if (first) {
        this.sneakerSize = first.options[sIdx];
        this.sneakerVariantId = first.id;
      } else if (variants.length) {
        this.sneakerSize = variants[0].options[sIdx];
        this.sneakerVariantId = variants[0].id;
      }
    }

    // ── Apparel actions ────────────────────────────────────────────

    _setGender(gender) {
      if (this.activeGender === gender) return;
      this.activeGender = gender;

      this.querySelectorAll('[data-gender]').forEach(btn => {
        const active = btn.dataset.gender === gender;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });

      this._onProductSwitch();
    }

    _setProductType(type) {
      if (this.activeType === type) return;
      this.activeType = type;

      this.querySelectorAll('[data-product-type]').forEach(btn => {
        const active = btn.dataset.productType === type;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });

      // Variant filter mode: update the type filter value and re-sync
      if (this.hasTypeFilter) {
        this.activeTypeFilterValue = this.typeValues[parseInt(type) - 1] || this.typeValues[0];
        this._syncApparelToSneaker();
        this._render();
        return;
      }

      this._onProductSwitch();
    }

    _onProductSwitch() {
      const product = this._activeProduct();
      if (!product) return;

      // Re-detect type option for new product (gender switch)
      if (this.hasTypeFilter) {
        this._detectTypeFilter(product);
      }

      if (!this.isMixMode) {
        if (!this.sneakerColour) {
          this.apparelColour = this._firstAvailableColour(product);
        } else {
          const match = this._matchColour(this.sneakerColour, product);
          if (match) {
            this.apparelColour = match;
          } else {
            this.isMixMode = true;
            this.apparelColour = this._firstAvailableColour(product);
          }
        }
      } else {
        // In mix mode — keep colour if available in type-filtered variants, else pick first
        const cIdx = this._colourIdx(product);
        const typeVariants = this._variantsForType(product);
        const has = typeVariants.some(v => this._n(v.options[cIdx]) === this._n(this.apparelColour));
        if (!has) this.apparelColour = this._firstAvailableColour(product);
      }

      this._autoApparelSize();
      this._render();
    }

    _enterMix() { this.isMixMode = true; this._render(); }

    _exitMix() {
      this.isMixMode = false;
      const product = this._activeProduct();
      if (this.sneakerColour) {
        const match = this._matchColour(this.sneakerColour, product);
        if (match) this.apparelColour = match;
      } else {
        this.apparelColour = this._firstAvailableColour(product);
      }
      this._autoApparelSize();
      this._render();
    }

    _pickApparelColour(colour) {
      this.apparelColour = colour;
      this._autoApparelSize();
      this._render();
    }

    _pickApparelSize(size) {
      this.apparelSize = size;
      const product = this._activeProduct();
      const v = this._findVariant(product, this.apparelColour, size);
      if (v) this.apparelVariantId = v.id;
      this._render();

      // Auto-collapse card 2 if apparel variant is resolved
      if (this.apparelVariantId) {
        this._collapseCard(2);
      }
    }

    // ── Add to cart ────────────────────────────────────────────────

    _addToCart(e) {
      e.preventDefault();
      const sneakerId = this.sneakerInput?.value;
      const apparelId = this.apparelInput?.value;
      if (!sneakerId || !apparelId) return;

      this.atcBtn.setAttribute('disabled', 'disabled');
      const bundleId = String(Date.now());
      const url = (typeof theme !== 'undefined' && theme.routes?.cartAdd) || '/cart/add.js';

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            { quantity: 1, id: parseInt(sneakerId), properties: { _bundleProduct: bundleId, _bundleTitle: this.bundleTitle } },
            { quantity: 1, id: parseInt(apparelId), properties: { _bundleProduct: bundleId, _bundleTitle: this.bundleTitle } },
          ]
        })
      })
        .then(r => r.json())
        .then(() => {
          this.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { addToCartBtn: this.atcBtn },
            bubbles: true,
          }));
          this.atcBtn.removeAttribute('disabled');
        })
        .catch(err => {
          console.error('BundleBuilder: cart error', err);
          this.atcBtn.removeAttribute('disabled');
        });
    }

    // ── Render ─────────────────────────────────────────────────────

    _render() {
      this._renderSneakerColours();
      this._renderSneakerSizes();
      this._renderApparelState();
      this._renderApparelSizes();
      this._renderSummary();
      this._updateInputs();
      this._checkAtc();
      this._updateCardSummaries();
    }

    _renderSneakerColours() {
      if (!this.sneakerColoursEl || !this.baseProduct) return;
      const bp = this.baseProduct;
      const cIdx = bp.colour_option_index;

      // No colour option — hide the swatch container
      if (cIdx < 0) {
        this.sneakerColoursEl.innerHTML = '';
        if (this.sneakerColourLabel) this.sneakerColourLabel.textContent = '';
        return;
      }

      const opt = bp.options_with_values[cIdx];
      if (!opt) return;

      const data = opt.values.map(colour => {
        const variant = bp.variants.find(v => this._n(v.options[cIdx]) === this._n(colour));
        const available = bp.variants.some(v => this._n(v.options[cIdx]) === this._n(colour) && v.available);
        return { colour, image: variant?.image, available };
      });

      this.sneakerColoursEl.innerHTML = data.map(d => {
        const active = this._n(d.colour) === this._n(this.sneakerColour);
        const cls = ['bb__swatch', active && 'is-active', !d.available && 'is-unavailable'].filter(Boolean).join(' ');
        const inner = d.image
          ? `<img src="${d.image}" alt="${d.colour}" loading="lazy" width="52" height="52">`
          : `<span class="bb__swatch-fallback"></span>`;
        return `<button type="button" class="${cls}" data-sneaker-colour-value="${d.colour}" title="${d.colour}" aria-label="${d.colour}" aria-pressed="${active}">${inner}</button>`;
      }).join('');

      if (this.sneakerColourLabel) {
        this.sneakerColourLabel.textContent = `— ${this.sneakerColour}`;
      }
    }

    _renderSneakerSizes() {
      if (!this.sneakerSizesEl || !this.baseProduct) return;
      const bp = this.baseProduct;
      const cIdx = bp.colour_option_index;
      const sIdx = bp.size_option_index;
      // No colour option — show all variants (single-colourway product)
      const variants = cIdx >= 0
        ? bp.variants.filter(v => this._n(v.options[cIdx]) === this._n(this.sneakerColour))
        : bp.variants;

      const seen = new Set();
      const sizes = [];
      for (const v of variants) {
        const size = v.options[sIdx];
        if (size && !seen.has(size)) { seen.add(size); sizes.push({ size, available: v.available }); }
      }

      this.sneakerSizesEl.innerHTML = sizes.map(s => {
        const active = this._n(s.size) === this._n(this.sneakerSize);
        const cls = ['bb__size-btn', active && 'is-active', !s.available && 'is-sold-out'].filter(Boolean).join(' ');
        return `<button type="button" class="${cls}" data-sneaker-size-value="${s.size}" aria-pressed="${active}">${s.size}</button>`;
      }).join('');
    }

    _renderApparelState() {
      const product = this._activeProduct();
      if (!product) return;

      const cIdx = this._colourIdx(product);
      const matchAvail = !!this._matchColour(this.sneakerColour, product);
      const activeVariant = this._findVariant(product, this.apparelColour, this.apparelSize)
        || this._variantsForColour(product, this.apparelColour)[0];

      if (this.isMixMode) {
        if (this.matchedState) this.matchedState.style.display = 'none';
        if (this.mixState) this.mixState.style.display = '';

        // Back link — only if matched colour exists
        if (this.backLink) this.backLink.style.display = matchAvail ? '' : 'none';

        // Mismatch note — only show if no apparel colour is selected yet
        if (this.mismatchNote) {
          const hasApparelSelection = this.apparelColour && this._variantsForColour(product, this.apparelColour).some(v => v.available);
          if (!matchAvail && !hasApparelSelection) {
            const tmpl = this.labels.mismatchTemplate || "That colour isn't available — pick from the options below";
            this.mismatchNote.textContent = tmpl
              .replace('{colour}', this._titleCase(this.sneakerColour));
            this.mismatchNote.style.display = '';
          } else {
            this.mismatchNote.style.display = 'none';
          }
        }

        // 50/50 preview images
        const sneakerV = this.baseProduct.variants.find(v => v.id === this.sneakerVariantId);
        if (this.previewSneaker && sneakerV?.image) {
          this.previewSneaker.innerHTML = `<img src="${sneakerV.image}" alt="Sneaker" loading="lazy">`;
        }
        if (this.previewApparel && activeVariant?.image) {
          this.previewApparel.innerHTML = `<img src="${activeVariant.image}" alt="${this.apparelColour}" loading="lazy">`;
        }

        if (this.mixColourValue) this.mixColourValue.textContent = `— ${this.apparelColour}`;

        // Colour swatches — filtered by type if applicable
        if (this.mixSwatches) {
          const typeVariants = this._variantsForType(product);
          // Get unique colours from type-filtered variants
          const seen = new Set();
          const colours = [];
          for (const v of typeVariants) {
            const c = v.options[cIdx];
            if (c && !seen.has(this._n(c))) { seen.add(this._n(c)); colours.push(c); }
          }

          this.mixSwatches.innerHTML = colours.map(colour => {
            const active = this._n(colour) === this._n(this.apparelColour);
            const avail = typeVariants.some(v => this._n(v.options[cIdx]) === this._n(colour) && v.available);
            const first = typeVariants.find(v => this._n(v.options[cIdx]) === this._n(colour));
            const cls = ['bb__swatch bb__swatch--sm', active && 'is-active', !avail && 'is-unavailable'].filter(Boolean).join(' ');
            const inner = first?.image ? `<img src="${first.image}" alt="${colour}" loading="lazy" width="30" height="30">` : '';
            return `<button type="button" class="${cls}" data-apparel-colour-value="${colour}" title="${colour}" aria-label="${colour}" aria-pressed="${active}">${inner}</button>`;
          }).join('');
        }
      } else {
        // Matched state
        if (this.matchedState) this.matchedState.style.display = '';
        if (this.mixState) this.mixState.style.display = 'none';

        if (this.apparelThumb && activeVariant?.image) {
          this.apparelThumb.innerHTML = `<img src="${activeVariant.image}" alt="${this.apparelColour}" loading="lazy" width="64" height="64">`;
        }
        if (this.apparelName) {
          this.apparelName.textContent = `${product.title} — ${this.apparelColour}`;
        }
        if (this.apparelMeta) {
          this.apparelMeta.textContent = this.labels.matchedLabel || 'Matching your sneaker';
        }
      }
    }

    _renderApparelSizes() {
      if (!this.apparelSizesEl) return;
      const product = this._activeProduct();
      if (!product) return;

      const sIdx = this._sizeIdx(product);
      const variants = this._variantsForColour(product, this.apparelColour);

      const seen = new Set();
      const sizes = [];
      for (const v of variants) {
        const size = v.options[sIdx];
        if (size && !seen.has(size)) { seen.add(size); sizes.push({ size, available: v.available }); }
      }

      this.apparelSizesEl.innerHTML = sizes.filter(s => s.available).map(s => {
        const active = this._n(s.size) === this._n(this.apparelSize);
        const cls = ['bb__size-btn', active && 'is-active'].filter(Boolean).join(' ');
        return `<button type="button" class="${cls}" data-apparel-size-value="${s.size}" aria-pressed="${active}">${s.size}</button>`;
      }).join('');
    }

    _renderSummary() {
      // Use the base product's actual price — no discount math
      const sneakerVariant = this.baseProduct?.variants.find(v => v.id === this.sneakerVariantId);
      const bundlePrice = sneakerVariant?.price || this.baseProduct?.variants[0]?.price || 0;

      if (this.summaryTotal) {
        this.summaryTotal.textContent = this._money(bundlePrice);
      }

      // Update included label with active product name
      if (this.summaryIncluded) {
        const product = this._activeProduct();
        this.summaryIncluded.textContent = product ? `${product.title} included` : '';
      }
    }

    _updateInputs() {
      if (this.sneakerInput) this.sneakerInput.value = this.sneakerVariantId || '';
      if (this.apparelInput) this.apparelInput.value = this.apparelVariantId || '';
    }

    _checkAtc() {
      if (!this.atcBtn) return;
      const sneakerV = this.baseProduct?.variants.find(v => v.id === this.sneakerVariantId);
      const product = this._activeProduct();
      const apparelV = product?.variants.find(v => v.id === this.apparelVariantId);

      const valid = sneakerV?.available && apparelV?.available;

      if (valid) {
        this.atcBtn.removeAttribute('disabled');
        if (this.atcText?.dataset.defaultText) {
          this.atcText.textContent = this.atcText.dataset.defaultText;
        }
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled');
        if (this.atcText) {
          this.atcText.textContent = this.labels.soldOutText || 'Sold out';
        }
      }
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}

